import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "../components/ThemeContext";
import { Container, Table, Button } from "react-bootstrap";
import NavMenu from "../components/NavMenu";
import axios from "axios";
import photo from "../img/no-data.svg";
import moment from "moment";
import { HiMiniViewfinderCircle } from "react-icons/hi2";

const getStatusStyle = (status) => {
  switch ((status || "").toLowerCase()) {
    case "submitted":
      return { bg: "#e6ffe6", text: "#2e7d32", dot: "#4caf50" };
    case "employee signed":
      return { bg: "#d0f0fd", text: "#0077b6", dot: "#0096c7" };
    case "both signed":
      return { bg: "#f3e5f5", text: "#6a1b9a", dot: "#9c27b0" };
    case "reviewing":
      return { bg: "#fff3e0", text: "#ef6c00", dot: "#ff9800" };
    case "pending":
      return { bg: "#ffebee", text: "#c62828", dot: "#f44336" };
    default:
      return { bg: "#eceff1", text: "#546e7a", dot: "#90a4ae" };
  }
};

const safeLower = (v) => (v == null ? "" : String(v)).trim().toLowerCase();

function History() {
  const [appraisal, setAppraisal] = useState([]);
  const [trackingHistory, setTrackingHistory] = useState([]);
  const [selectedAppraisal, setSelectedAppraisal] = useState("");
  const [appraisalNames, setAppraisalNames] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const recordsPerPage = 10;
  const { isDark } = useTheme();

  useEffect(() => {
    const fetchAppraisal = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No auth token found in localStorage");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getAllAppraisal`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.data?.success) {
          console.error(response.data?.message || "Failed to fetch appraisal");
          return;
        }

        const allAppraisals = response.data.data || [];
        const user = JSON.parse(localStorage.getItem("user")) || {};

        const userName = safeLower(user?.user_name);        
        const userDeptOrRole = safeLower(user?.description);  

        let filteredAppraisals = [];

        if (userDeptOrRole === "hr") {
          filteredAppraisals = allAppraisals;
        } else {
          const isSupervisor = allAppraisals.some(
            (item) => safeLower(item.supervisor_name) === userName
          );

          if (isSupervisor) {
            filteredAppraisals = allAppraisals.filter((item) => {
              const sup = safeLower(item.supervisor_name);
              const staff = safeLower(item.staff_name);
              return sup === userName || staff === userName;
            });
          } else {
            filteredAppraisals = allAppraisals.filter(
              (item) => safeLower(item.staff_name) === userName
            );
          }
        }

        setAppraisal(filteredAppraisals);

        const uniqueAppraisals = [
          ...new Set(
            filteredAppraisals
              .map((a) => a.appraisal_name)
              .filter(Boolean)
          ),
        ];
        setAppraisalNames(uniqueAppraisals);

        setCurrentPage(1);
        setTrackingHistory([]);
      } catch (error) {
        console.error("Error fetching appraisal data:", error);
      }
    };

    fetchAppraisal();
  }, []);

  const filteredRecords = useMemo(() => {
    return selectedAppraisal
      ? appraisal.filter((a) => a.appraisal_name === selectedAppraisal)
      : appraisal;
  }, [appraisal, selectedAppraisal]);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  const fetchTrackingHistory = async (send_id) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      console.error("No auth token found in localStorage");
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/getHistory`,
        {
          params: { send_id },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.success) setTrackingHistory(response.data.data || []);
      else setTrackingHistory([]);
    } catch (error) {
      console.error("Error fetching tracking history:", error);
      setTrackingHistory([]);
    }
  };

  const renderTable = (data) => (
    <Table
      bordered
      responsive
      hover
      size="sm"
      className={`mb-0 mt-2 ${
        isDark ? "table-dark text-light" : "bg-white text-dark"
      }`}
    >
      <thead>
        <tr className="text-center">
          <th style={{ width: 60 }}>No</th>
          <th>Name</th>
          <th>Department</th>
          <th>Supervisor</th>
          <th>Start Date</th>
          <th>Due Date</th>
          <th>Status</th>
          <th style={{ width: 120 }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td colSpan="8" className="text-center py-4">
              <div className="d-flex flex-column align-items-center">
                <img
                  src={photo}
                  alt="Illustration"
                  className="img-fluid mb-3"
                  width="100px"
                />
                <div className={`${isDark ? "text-secondary" : "text-muted"}`}>
                  No PA Form found
                </div>
              </div>
            </td>
          </tr>
        ) : (
          data.map((emp, index) => {
            const { bg, text, dot } = getStatusStyle(emp.status || "");
            return (
              <tr key={emp.send_id || index} className="align-middle text-center">
                <td>{indexOfFirstRecord + index + 1}</td>

                <td className="text-start">
                  <div className="fw-semibold">{emp.staff_name}</div>
                  <div
                    className={`${isDark ? "text-secondary" : "text-muted"}`}
                    style={{ fontSize: "12px" }}
                  >
                    {emp.appraisal_name || "-"}
                  </div>
                </td>

                <td>{emp.department || "-"}</td>
                <td>{emp.supervisor_name || "-"}</td>
                <td>{emp.start_date ? moment(emp.start_date).format("MMM-YYYY") : "-"}</td>
                <td>{emp.due_date ? moment(emp.due_date).format("MMM-YYYY") : "-"}</td>

                <td>
                  <div
                    className="d-inline-flex align-items-center px-2 py-1 rounded-pill"
                    style={{
                      backgroundColor: bg,
                      color: text,
                      fontWeight: 500,
                      textTransform: "capitalize",
                      fontSize: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "10px",
                        height: "10px",
                        backgroundColor: dot,
                        borderRadius: "50%",
                        marginRight: "8px",
                      }}
                    />
                    {emp.status || "-"}
                  </div>
                </td>

                <td>
                  <Button
                    variant="outline-light"
                    size="sm"
                    style={{ color: "#B31E8D", borderColor: "#B31E8D" }}
                    onClick={() => fetchTrackingHistory(emp.send_id)}
                  >
                    <HiMiniViewfinderCircle size={20} />
                    &nbsp; Track
                  </Button>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </Table>
  );

  return (
    <>
      <NavMenu />
      <Container fluid className="min-vh-100 p-4">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h5 className="header-title mb-2 mb-md-0 fw-bold">
            Appraisal Tracking
          </h5>

          <select
            className={`form-select form-select-sm w-auto ${
              isDark ? "bg-dark text-light border-secondary" : ""
            }`}
            value={selectedAppraisal}
            onChange={(e) => {
              setSelectedAppraisal(e.target.value);
              setCurrentPage(1);
              setTrackingHistory([]);
            }}
          >
            <option value="">All Appraisals</option>
            {appraisalNames.map((name, idx) => (
              <option key={idx} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="row">
          <div className="col-md-8">
            {renderTable(currentRecords)}

            {filteredRecords.length > recordsPerPage && (
              <div className="d-flex justify-content-center mt-3">
                <div className="pagination-container d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                  >
                    Prev
                  </Button>
                  <span
                    className="px-2 pt-1"
                    style={{ color: isDark ? "#fff" : "#000" }}
                  >
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="col-md-4">
            <div
              className={`p-3 rounded ${
                isDark
                  ? "bg-dark text-light border-secondary"
                  : "bg-white text-dark"
              }`}
              style={{ border: isDark ? "1px solid #444" : "1px solid #ccc" }}
            >
              <h6 className="fw-bold mb-4">Tracking Appraisal Processes</h6>

              {trackingHistory.length === 0 ? (
                <div className={isDark ? "text-secondary" : "text-muted"}>
                  Select an appraisal to view history.
                </div>
              ) : (
                <div className="tracking-timeline">
                  {trackingHistory.map((item, index) => (
                    <div
                      className={`tracking-item ${index === 0 ? "active" : ""}`}
                      key={item.history_id || index}
                    >
                      <div className={`icon-dot ${index === 0 ? "" : "faded"} `} />
                      <div>
                        <div
                          className={`timestamp ${
                            index === 0
                              ? "text-success fw-semibold"
                              : isDark
                              ? "text-secondary"
                              : "text-muted"
                          }`}
                        >
                          {moment(item.created_at).format("DD MMM YY HH:mm")}
                        </div>
                        <div
                          className={`message ${
                            index === 0
                              ? "text-success fw-semibold"
                              : isDark
                              ? "text-light"
                              : "text-muted"
                          }`}
                        >
                          {item.description}{" "}
                          {item.created_by_name ? `by ${item.created_by_name}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </>
  );
}

export default History;
